import config from '@config.client';
import {WeatherForecastLocation} from '@/app/interfaces/weather';
import {StoreState} from '@/app/store';
import {selectDomainConfig} from '@/app/store/config/selectors';
import {ActionsDependencies} from '@/app/store/ActionsDependencies';

const {relinkRegionTopLocationsCount, relinkCountryTopLocationsCount} = config;

// A one spare required for case when the list contains the currently viewed location
const fetchRelinkRegionItemsCount = relinkRegionTopLocationsCount + 1;

// Spare items required for case when the list contains the top locations of the region
const fetchRelinkCountryTopLocationsCount = relinkCountryTopLocationsCount + fetchRelinkRegionItemsCount;

export async function settlementExtraDataFetcher({
	language,
	location,
	context,
	getState,
}: {
	language: string;
	location: WeatherForecastLocation;
	context: ActionsDependencies;
	getState(): StoreState;
}) {
	const {backendAPI} = context;
	const state = getState();

	const {countryId} = selectDomainConfig(state);
	const regionId = location.superior.region?.id;

	let [regionTopLocationWeather, countryTopLocations] = await Promise.all([
		// Request to get list of largest locations of the current region and them weather
		// Use cached value for locations list request because it is static.
		regionId
			? backendAPI
					.cachedRequest(backendAPI.location.getLargestSettlements, [
						regionId,
						language,
						fetchRelinkRegionItemsCount,
						['title', 'titleLc', 'flags'],
					])
					// Subsequent to get the weather
					.then(locations => backendAPI.forecast.getWeatherForLocations(locations, language))
			: Promise.resolve([]),

		// Request to get list of largest locations of the domain county
		// Use cached value if allowed, it is static
		backendAPI.cachedRequest(backendAPI.location.getLargestSettlements, [
			countryId,
			language,
			fetchRelinkCountryTopLocationsCount,
			['title', 'titleLc'],
		]),
	]);

	// Clean an extra items from the locations list
	// The extra are duplicates with the current location and between the lists
	regionTopLocationWeather = regionTopLocationWeather
		.filter(item => item.location.id !== location.id)
		.slice(0, relinkRegionTopLocationsCount);

	const regionTopLocationId = regionTopLocationWeather.map(item => item.location.id);
	countryTopLocations = countryTopLocations
		.filter(item => item.id !== location.id || regionTopLocationId.includes(item.id))
		.slice(0, relinkCountryTopLocationsCount);

	return {regionTopLocationWeather, countryTopLocations};
}
